import * as React from 'react';
import CalendarDayList from './CalendarDayList';
import CalendarSurfaceConfig from '../interfaces/CalendarSurfaceConfig';
import MonthSurface from './MonthSurface';
import strings from 'owa-strings/owa-calendar-strings';
import SurfaceStore from '../interfaces/SurfaceStore';
import { addSkipLinkRegion } from 'owa-skip-link/lib/utils/addSkipLinkRegion';
import { DateRangeType } from 'office-ui-fabric-react/lib/utilities/dateValues/DateValues';
import { format } from 'owa-strings';
import { logMountingPerformance } from 'owa-analytics';
import { logUsage } from 'owa-analytics';
import { observer } from 'mobx-react';

let styles = require('./CalendarSurface.scss');

/**
 * Props for calendar surface
 */
export interface CalendarSurfaceProps {
    config: CalendarSurfaceConfig;
    store: SurfaceStore;
}

/**
 * Main calendar surface component with navigation.
 */
@logMountingPerformance('CalendarSurfaceView')
@observer
export default class CalendarSurface extends React.Component<CalendarSurfaceProps, {}> {
    private prevDateRangeType: DateRangeType;
    private surfaceRef: HTMLElement;
    private popupBoundsFunction: (anchorElement: HTMLElement) => ClientRect;

    componentDidMount() {
        this.prevDateRangeType = this.props.store.dateRangeType;
        this.logUsageWithDateRangeType();
    }

    componentDidUpdate(prevProps: CalendarSurfaceProps) {
        if (this.prevDateRangeType != this.props.store.dateRangeType) {
            this.logUsageWithDateRangeType();
            this.prevDateRangeType = this.props.store.dateRangeType;
        }
    }

    render() {
        let { store, config } = this.props;

        let view =
            store.dateRangeType !== DateRangeType.Month ? (
                <CalendarDayList
                    config={config.dayViewConfig}
                    store={store}
                    ref={el => (this.popupBoundsFunction = el && el.getPopupBounds)}
                />
            ) : (
                <MonthSurface config={config.monthViewConfig} store={store} />
            );

        return (
            <div className={styles.mainContent}>
                {addSkipLinkRegion(
                    format(strings.jumpToRegion, strings.calendarViewRegionName),
                    'main',
                    <div className={styles.surfaceContainer}>{view}</div>,
                    strings.calendarViewRegionName,
                    el => {
                        this.surfaceRef = el as HTMLElement;
                    }
                )}
            </div>
        );
    }

    private logUsageWithDateRangeType() {
        logUsage('LoadCalendarView', {
            dateRangeType: this.props.store.dateRangeType,
            splitMode:
                this.props.config.dayViewConfig.supportSplitView &&
                this.props.config.dayViewConfig.supportSplitView(),
        });
    }

    public getPopupBounds(anchorElement: HTMLElement): ClientRect {
        if (this.popupBoundsFunction) {
            return this.popupBoundsFunction(anchorElement);
        }
        return null;
    }

    /**
     * Sets focus to the surface root element
     */
    public focusSurfaceRoot() {
        if (this.surfaceRef) {
            this.surfaceRef.focus();
        }
    }
}
